'use client';

import { useEffect } from 'react';

/**
 * DragClickGuard — GLOBALER Fix für "Modal schließt beim Text-Markieren".
 *
 * Problem (betrifft ALLE Overlays mit onClick={onClose} bzw.
 * onClick + e.target === e.currentTarget):
 *   Drückt man die Maus INNERHALB des Modals (z. B. um Text zu markieren),
 *   zieht den Cursor aus dem Modal heraus und lässt dort los, feuert der
 *   Browser den `click` auf dem nächsten gemeinsamen Vorfahren — das ist der
 *   Backdrop. Der `target === currentTarget`-Check hilft nicht, weil der
 *   Backdrop dann tatsächlich das Click-Target ist → Modal schließt.
 *
 * Native Konvention (macOS/Windows): Ein Klick gilt nur, wenn Drücken UND
 *   Loslassen auf demselben Control passieren. Loslassen außerhalb = Abbruch.
 *
 * Dieser Guard setzt genau das global durch:
 *   - pointerdown-Target wird gemerkt (Capture-Phase).
 *   - Feuert ein `click` auf einem ANDEREN Element als dem pointerdown-Target
 *     (und liegt das Down-Target nicht im selben interaktiven Control wie das
 *     Click-Target, z. B. <span> im <button>), wird der Click verworfen,
 *     BEVOR ihn irgendein React-Handler (Overlay onClick etc.) sieht.
 *   - Echte Klicks (down === click-Target) und Keyboard-/Programmatic-Clicks
 *     (kein pointerdown) bleiben unberührt.
 *
 * NACHTRAG 2026-09-01 — "1. Klick auf einen Button tut sichtbar nichts,
 * 2. Klick funktioniert" (KHG-Bonusanforderungs-Modal, aber generisch bei
 * JEDER Button-Gruppe mit knappem Abstand, z.B. "Abbrechen"/"Speichern"):
 *   Browser berechnen das Click-Target bei Press/Release auf verschiedenen
 *   Elementen über den GEMEINSAMEN VORFAHREN — nicht über den pointerdown-
 *   Target. Beginnt der Mousedown DIREKT auf einem Button und driftet die
 *   Maus durch ganz normales Hand-Zittern (real, aber von Playwright/CDP-
 *   Klicks mit identischen Down/Up-Koordinaten NIE reproduziert) beim
 *   Loslassen ein paar Pixel über den Button-Rand hinaus in die Lücke zum
 *   Nachbar-Button, landet der native Click auf dem umschließenden
 *   `<div className="flex gap-2">` — NICHT auf dem Button. Das passiert
 *   AUCH OHNE diesen Guard (reines Browser-Verhalten!), aber der Guard hat
 *   bereits die einzige Stelle, die weiß, wo der Press wirklich begann —
 *   und kann den Klick daher korrekt NACHTRÄGLICH auf das echte Ziel
 *   umleiten statt ihn nur zu verwerfen: War der Mousedown SELBST bereits
 *   auf einem echten interaktiven Control (nicht auf Text/Content) UND
 *   landete der Release NICHT zufällig auf einem ANDEREN echten Control,
 *   wird `down.click()` nachgefeuert — eine vollständige neue
 *   Capture/Target/Bubble-Runde auf dem tatsächlich gemeinten Element.
 *   Die ursprüngliche Drag-aus-Text-Erkennung (Press auf reinem Content,
 *   Release auf dem Backdrop) bleibt unverändert: dort ist `down` kein
 *   interaktives Control, also greift die Umleitung nicht — nur das
 *   Verwerfen wie bisher.
 *
 * Scope: Shell-Dokument UND alle same-origin iframes. Module (z. B.
 * mail-hosting unter /modules/{slug}/) laufen in iframes — deren Events
 * erreichen das Parent-Dokument nie. Der Guard injiziert sich daher in
 * jedes same-origin iframe (bestehende + dynamisch hinzugefügte, Re-Install
 * bei jeder iframe-Navigation via load-Event). Cross-origin iframes werden
 * still übersprungen.
 *
 * Wirkt auf alle bestehenden UND zukünftigen Modals/Overlays — ohne dass
 * jede einzelne Komponente angepasst werden muss. Radix-Dialogs sind ohnehin
 * nicht betroffen (schließen auf pointerdown-outside, nicht auf click).
 */
const INTERACTIVE_SELECTOR =
  'a,button,input,select,textarea,option,label,summary,' +
  '[role="button"],[role="link"],[role="menuitem"],[contenteditable="true"],' +
  '[data-dragclick-allow]';

function installGuard(doc) {
  if (!doc || doc.__xoffiDragClickGuard) return;
  doc.__xoffiDragClickGuard = true;

  let downTarget = null;

  const onPointerDown = (e) => {
    downTarget = e.target;
  };

  const onClick = (e) => {
    const down = downTarget;
    downTarget = null;

    // Kein vorheriges pointerdown (Keyboard-Enter, element.click(), Screenreader)
    // oder normaler Klick auf dasselbe Element → durchlassen.
    if (!down || down === e.target) return;

    // Drag-Click: Click landete auf einem anderen Element (gemeinsamer Vorfahre).
    // Ausnahme: Der Druck begann innerhalb desselben interaktiven Controls,
    // auf dem der Click landet (z. B. <span> in <button>) → echter Control-Klick.
    const downCtrl =
      down.nodeType === 1 && typeof down.closest === 'function'
        ? down.closest(INTERACTIVE_SELECTOR)
        : null;
    if (downCtrl && e.target === downCtrl) return;

    // Mini-Drift-Korrektur: Der Mousedown begann bereits DIREKT auf einem
    // echten Control (Button/Input/Select/Label/...) — eindeutige Absicht,
    // MIT diesem Control zu interagieren. Landet der Release wegen ein paar
    // Pixeln Hand-Zittern nicht mehr auf dem Control selbst, sondern auf
    // einem NICHT-interaktiven Nachbarn (z. B. der Gap-Lücke zwischen zwei
    // Buttons), war das kein Abbruch-Drag — den echten Klick nachfeuern,
    // statt ihn zu verwerfen. Landete der Release dagegen auf einem ANDEREN
    // echten Control, bleibt es beim Verwerfen (Zwei-Buttons-Mehrdeutigkeit).
    if (downCtrl === down) {
      // WICHTIG: e.target ist hier NICHT "das Element unter der Maus beim
      // Loslassen" — bei Press/Release auf zwei GESCHWISTER-Buttons (z. B.
      // Erstellen → Abbrechen) berechnet der Browser den Click-Target IMMER
      // als deren gemeinsamen Vorfahren (die umschließende <div>), UNABHÄNGIG
      // davon, über welchem der beiden Buttons losgelassen wurde. e.target
      // würde daher fälschlich als "kein Control" durchgehen und JEDEN
      // Nachbar-Button-Release wie eine Gap-Lücke behandeln → würde bei
      // Release auf "Abbrechen" trotzdem "Erstellen" nachfeuern. Deshalb
      // real nachschauen, was tatsächlich unter dem Loslass-Punkt liegt.
      const releaseEl =
        typeof doc.elementFromPoint === 'function'
          ? doc.elementFromPoint(e.clientX, e.clientY)
          : e.target;
      const targetCtrl =
        releaseEl && releaseEl.nodeType === 1 && typeof releaseEl.closest === 'function'
          ? releaseEl.closest(INTERACTIVE_SELECTOR)
          : null;
      if (!targetCtrl || targetCtrl === down) {
        e.stopPropagation();
        e.preventDefault();
        down.click(); // eigene volle Capture/Target/Bubble-Runde auf dem echten Ziel
        return;
      }
    }

    // Gezogener Klick (Text markiert, Drag, außerhalb losgelassen) → verwerfen.
    e.stopPropagation();
    e.preventDefault();
  };

  const onBlur = () => {
    downTarget = null; // Maus außerhalb des Fensters losgelassen → Reset
  };

  doc.addEventListener('pointerdown', onPointerDown, true);
  doc.addEventListener('click', onClick, true);
  doc.defaultView?.addEventListener('blur', onBlur);
}

export default function DragClickGuard() {
  useEffect(() => {
    installGuard(document);

    const installIntoIframe = (iframe) => {
      const tryInstall = () => {
        try {
          // contentDocument-Zugriff wirft bei cross-origin → still überspringen
          if (iframe.contentDocument) installGuard(iframe.contentDocument);
        } catch {
          /* cross-origin iframe — skip */
        }
      };
      tryInstall(); // iframe evtl. schon geladen
      iframe.addEventListener('load', tryInstall); // (Re-)Navigation im iframe
    };

    document.querySelectorAll('iframe').forEach(installIntoIframe);

    // iframes, die später gemountet werden (Module-Pages bauen die iframe-src
    // erst nach Token-Fetch) → MutationObserver fängt sie ab.
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        for (const node of m.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.tagName === 'IFRAME') installIntoIframe(node);
          node.querySelectorAll?.('iframe').forEach(installIntoIframe);
        }
      }
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return null;
}
